import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  FolderKanban,
  MoreVertical,
  Trash2,
  Bot,
  Crown,
  ExternalLink,
  ChevronRight,
  Sparkles,
  StickyNote,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { apiClient, useAuth } from "../App";
import { toast } from "sonner";

const parseProjectNotes = (notesValue, fallbackDate = null) => {
  if (!notesValue || typeof notesValue !== "string") return [];

  try {
    const parsed = JSON.parse(notesValue);
    if (Array.isArray(parsed)) {
      return parsed.filter(
        (entry) => entry && typeof entry.text === "string" && entry.text.trim().length > 0
      );
    }
  } catch {
    // old plain-text notes format
  }

  if (notesValue.trim().length === 0) return [];

  return [
    {
      id: `legacy-${Date.now()}`,
      text: notesValue,
      created_at: fallbackDate || new Date().toISOString(),
    },
  ];
};

const MyProjectsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingNotes, setEditingNotes] = useState(null);
  const [notesValue, setNotesValue] = useState("");
  const [expandedNotes, setExpandedNotes] = useState({});
  const debugPlanEnabled = localStorage.getItem("plan_override_enabled") === "true";
  const overridePlan = (localStorage.getItem("test_plan_override") || "").trim().toLowerCase();
  const hasAdminPin = Boolean((localStorage.getItem("admin_plan_pin") || "").trim());
  const canUsePlanOverride = debugPlanEnabled && hasAdminPin;
  const effectivePlan =
    canUsePlanOverride && ["free", "pro", "full_access"].includes(overridePlan)
      ? overridePlan
      : (user?.plan || "free").toLowerCase();

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get("/projects");
      const mappedProjects = (Array.isArray(response.data) ? response.data : []).map((project) => {
        const projectId = project.project_id || project.id;
        const createdAt = project.created_at || project.saved_at || project.updated_at || null;
        const notesEntries = parseProjectNotes(project.notes, createdAt);

        return {
          ...project,
          project_id: projectId,
          opportunity_name: project.opportunity_name || project.name || "Saved Project",
          created_at: createdAt,
          status: project.status || "idea",
          notes: project.notes || "",
          notes_entries: notesEntries,
        };
      });

      setProjects(mappedProjects);
    } catch (error) {
      console.error("fetchProjects error:", error);
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (projectId, newStatus) => {
    try {
      await apiClient.put(`/projects/${projectId}`, { status: newStatus });

      setProjects((prev) =>
        prev.map((p) => (p.project_id === projectId ? { ...p, status: newStatus } : p))
      );
      toast.success("Status updated");
    } catch (error) {
      console.error("handleStatusChange error:", error);
      toast.error("Failed to update status");
    }
  };

  const handleSaveNotes = async (projectId) => {
    try {
      const project = projects.find((p) => p.project_id === projectId);
      const trimmedNote = notesValue.trim();

      if (!trimmedNote) {
        toast.error("Please write a note first");
        return;
      }

      const existingEntries = project?.notes_entries || [];
      const newEntry = {
        id: `${Date.now()}`,
        text: trimmedNote,
        created_at: new Date().toISOString(),
      };
      const updatedEntries = [newEntry, ...existingEntries];
      const serializedNotes = JSON.stringify(updatedEntries);

      await apiClient.put(`/projects/${projectId}`, { notes: serializedNotes });

      setProjects((prev) =>
        prev.map((p) =>
          p.project_id === projectId
            ? {
                ...p,
                notes: serializedNotes,
                notes_entries: updatedEntries,
              }
            : p
        )
      );
      setEditingNotes(null);
      setNotesValue("");
      toast.success("Note saved");
    } catch (error) {
      console.error("handleSaveNotes error:", error);
      toast.error("Failed to save notes");
    }
  };

  const handleDeleteProject = async (projectId) => {
    if (!window.confirm("Are you sure you want to remove this project?")) return;

    try {
      await apiClient.delete(`/projects/${projectId}`);

      setProjects((prev) => prev.filter((p) => p.project_id !== projectId));
      toast.success("Project removed");
    } catch (error) {
      console.error("handleDeleteProject error:", error);
      toast.error("Failed to remove project");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "idea":
        return "bg-zinc-500/20 text-zinc-300";
      case "mvp":
        return "bg-[#7C3AED]/20 text-[#7C3AED]";
      case "launch":
        return "bg-green-500/20 text-green-400";
      default:
        return "bg-zinc-500/20 text-zinc-300";
    }
  };

  const openCofounder = (project) => {
    if (effectivePlan === "free") {
      toast.message("Cofounder is a Pro feature. Upgrade to continue.");
      navigate("/dashboard/settings/billing");
      return;
    }
    navigate(`/dashboard/ai-builder/${project.opportunity_id}?projectId=${project.project_id}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#7C3AED] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="px-4 py-5 md:p-8 lg:p-12">
      <div className="mb-6 md:mb-8">
        <h1
          className="text-2xl md:text-4xl font-bold tracking-tight text-white mb-2"
          style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
        >
          My Projects
        </h1>
        <p className="text-zinc-400">Track your saved opportunities and progress</p>
      </div>

      {projects.length === 0 ? (
        <div className="bg-[#111111] border border-[#1F1F1F] rounded-2xl md:rounded-[2rem] p-8 md:p-12 text-center">
          <FolderKanban className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
          <h2
            className="text-xl font-semibold text-white mb-2"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
          >
            No projects yet
          </h2>
          <p className="text-zinc-500 mb-6 max-w-md mx-auto">
            Save opportunities you're interested in to track your progress from idea to launch
          </p>
          <Button
            className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white font-semibold rounded-full px-8"
            onClick={() => navigate("/dashboard/opportunities")}
            data-testid="browse-opportunities-btn"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            Browse Opportunities
          </Button>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4 mb-6 md:mb-8">
            {["idea", "mvp", "launch"].map((status) => {
              const count = projects.filter((p) => p.status === status).length;
              return (
                <div key={status} className="bg-[#111111] border border-[#1F1F1F] rounded-xl md:rounded-2xl p-4 md:p-6">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium uppercase ${getStatusColor(status)}`}>
                    {status}
                  </span>
                  <p
                    className="text-2xl md:text-3xl font-bold text-white mt-3 md:mt-4"
                    style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
                  >
                    {count}
                  </p>
                  <p className="text-xs text-zinc-500">projects</p>
                </div>
              );
            })}
          </div>

          <div className="space-y-4">
            {projects.map((project) => (
              <div
                key={project.project_id}
                className="bg-[#111111] border border-[#1F1F1F] rounded-2xl md:rounded-[2rem] p-4 md:p-6 hover:border-[#2A2A2A] transition-all"
                data-testid={`project-card-${project.project_id}`}
              >
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between sm:gap-4 mb-4">
                  <div className="flex-1 min-w-0">
                    <button
                      type="button"
                      className="text-left text-lg font-semibold text-white mb-1 hover:text-[#7C3AED] cursor-pointer transition-colors"
                      onClick={() => navigate(`/dashboard/opportunities/${project.opportunity_id}`)}
                      style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
                    >
                      {project.opportunity_name}
                    </button>
                    <p className="text-xs text-zinc-500">
                      Added{" "}
                      {project.created_at
                        ? new Date(project.created_at).toLocaleDateString()
                        : "recently"}
                    </p>
                  </div>

                  <div className="flex items-center gap-3 shrink-0 self-start sm:self-auto">
                    <Select
                      value={project.status}
                      onValueChange={(value) => handleStatusChange(project.project_id, value)}
                    >
                      <SelectTrigger
                        className={`w-28 h-8 rounded-full text-xs font-medium border-none ${getStatusColor(project.status)}`}
                        data-testid={`status-select-${project.project_id}`}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#111111] border-[#1F1F1F]">
                        <SelectItem value="idea">Idea</SelectItem>
                        <SelectItem value="mvp">MVP</SelectItem>
                        <SelectItem value="launch">Launch</SelectItem>
                      </SelectContent>
                    </Select>

                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-zinc-500 hover:text-white"
                          data-testid={`project-menu-${project.project_id}`}
                        >
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-[#111111] border-[#1F1F1F]">
                        <DropdownMenuItem
                          className="text-zinc-300 hover:text-white cursor-pointer"
                          onClick={() => navigate(`/dashboard/opportunities/${project.opportunity_id}`)}
                        >
                          <ExternalLink className="w-4 h-4 mr-2" />
                          View Details
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="text-zinc-300 hover:text-white cursor-pointer"
                          onClick={() => openCofounder(project)}
                        >
                          <Bot className="w-4 h-4 mr-2" />
                          <span className="inline-flex items-center gap-1.5">
                            Open Cofounder
                            {effectivePlan === "free" ? (
                              <Crown className="w-3.5 h-3.5 text-[#A78BFA]" />
                            ) : null}
                          </span>
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="text-red-400 hover:text-red-300 cursor-pointer"
                          onClick={() => handleDeleteProject(project.project_id)}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Remove
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>

                {/* Notes Section */}
                <div className="mt-5 pt-5 border-t border-white/[0.05]">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <StickyNote className="w-4 h-4 text-[#7C3AED]" />
                      <span className="text-sm font-medium text-white">Notes</span>
                    </div>

                    {editingNotes !== project.project_id && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-zinc-400 hover:text-white hover:bg-white/5 rounded-lg"
                        onClick={() => {
                          setEditingNotes(project.project_id);
                          setNotesValue("");
                        }}
                        data-testid={`edit-notes-${project.project_id}`}
                      >
                        Add note
                      </Button>
                    )}
                  </div>

                  {editingNotes === project.project_id ? (
                    <div className="space-y-3">
                      <Textarea
                        value={notesValue}
                        onChange={(e) => setNotesValue(e.target.value)}
                        placeholder="Add a new note, update, idea, blocker, or next step..."
                        className="bg-[#161616] border-[#1F1F1F] rounded-xl text-white placeholder:text-zinc-500 min-h-[120px]"
                        data-testid={`notes-input-${project.project_id}`}
                      />
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white rounded-lg"
                          onClick={() => handleSaveNotes(project.project_id)}
                          data-testid={`save-notes-${project.project_id}`}
                        >
                          Save
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-zinc-400 hover:text-white rounded-lg"
                          onClick={() => setEditingNotes(null)}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="rounded-2xl bg-[#161616] border border-[#1F1F1F] p-4 transition-colors" data-testid={`edit-notes-${project.project_id}`}>
                      <div className="flex justify-between items-center mb-3">
                        <span className="text-xs text-zinc-500">
                          {project.notes_entries?.length || 0} notes
                        </span>
                        <button
                          className="text-xs text-[#7C3AED] hover:underline"
                          onClick={() =>
                            setExpandedNotes((prev) => ({
                              ...prev,
                              [project.project_id]: !prev[project.project_id],
                            }))
                          }
                        >
                          {expandedNotes[project.project_id] ? "Collapse" : "Expand"}
                        </button>
                      </div>
                      {project.notes_entries && project.notes_entries.length > 0 ? (
                        <>
                          {expandedNotes[project.project_id] && (
                            <div className="space-y-3 max-h-[220px] overflow-y-auto pr-1">
                              {project.notes_entries.map((note) => (
                                <div
                                  key={note.id}
                                  className="rounded-xl bg-black/20 border border-white/[0.04] p-3"
                                >
                                  <div className="text-[11px] uppercase tracking-wide text-zinc-500 mb-2">
                                    {new Date(note.created_at).toLocaleString()}
                                  </div>
                                  <p className="text-sm text-zinc-200 whitespace-pre-wrap leading-6 break-words">
                                    {note.text}
                                  </p>
                                </div>
                              ))}
                            </div>
                          )}
                        </>
                      ) : (
                        <div
                          className="flex items-center gap-2 text-zinc-500 cursor-pointer"
                          onClick={() => {
                            setEditingNotes(project.project_id);
                            setNotesValue("");
                          }}
                        >
                          <StickyNote className="w-4 h-4" />
                          <span className="text-sm">No notes yet. Click here to add one.</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Quick Actions */}
                <div className="mt-4 flex gap-3">
                  <Button
                    variant="outline"
                    size="sm"
                    className="bg-white/5 border-[#1F1F1F] text-white hover:bg-white/10 rounded-lg"
                    onClick={() => openCofounder(project)}
                    data-testid={`ai-builder-btn-${project.project_id}`}
                  >
                    <Bot className="w-4 h-4 mr-2" />
                    <span className="inline-flex items-center gap-1.5">
                      Cofounder
                      {effectivePlan === "free" ? (
                        <Crown className="w-3.5 h-3.5 text-[#A78BFA]" />
                      ) : null}
                    </span>
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-zinc-400 hover:text-white"
                    onClick={() => navigate(`/dashboard/opportunities/${project.opportunity_id}`)}
                    data-testid={`view-details-btn-${project.project_id}`}
                  >
                    View Details
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MyProjectsPage;
